"use client";

// Token storage keys
const TOKEN_KEY = 'fastrax_auth_token';
const TOKEN_EXPIRY_KEY = 'fastrax_token_expiry';
const USER_DATA_KEY = 'fastrax_user_data';

// Token expiration buffer (5 minutes before actual expiry)
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

export interface AuthToken {
  token: string;
  expiresAt: string;
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    district?: string;
    customerId?: string;
  };
}

export interface StoredTokenData {
  token: string;
  expiresAt: string;
  user: AuthToken['user'];
}

export interface LoginResponse {
  date: string | null;
  email: string | null;
  employeeId: string | null;
  employeeName: string | null;
  error: string | null;
  password: string | null;
  rememberMe: boolean | null;
  successful: boolean;
  token: string;
}

/**
 * Parse JWT token to extract payload
 */
const parseJwt = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    throw new Error('Invalid token format');
  }
};

/**
 * Extract user data from JWT token
 */
const extractUserFromToken = (token: string): AuthToken['user'] => {
  const payload = parseJwt(token);
  
  console.log('🔍 Extracting user from token:', {
    payload: payload,
    ContactId: payload.ContactId,
    UserEmail: payload.UserEmail,
    ContactName: payload.ContactName,
    CustomerName: payload.CustomerName,
    CustomerId: payload.CustomerId,
    timestamp: new Date().toISOString()
  });
  
  const user = {
    id: payload.ContactId || payload.sub || '',
    email: payload.UserEmail || payload.email || '',
    firstName: payload.ContactName?.split(' ')[0] || '',
    lastName: payload.ContactName?.split(' ').slice(1).join(' ') || '',
    company: payload.CustomerName || '',
    district: '', // You might need to get this from another endpoint
    customerId: payload.CustomerId || '',
  };
  
  console.log('✅ Extracted user data:', {
    user: user,
    timestamp: new Date().toISOString()
  });
  
  return user;
};

/**
 * Extract expiration from JWT token
 */
const extractExpirationFromToken = (token: string): string => {
  const payload = parseJwt(token);
  const expTimestamp = payload.exp;
  
  if (!expTimestamp) {
    throw new Error('Token does not contain expiration information');
  }
  
  // Convert Unix timestamp to ISO string
  return new Date(expTimestamp * 1000).toISOString();
};

/**
 * Store authentication token from login response
 */
export const storeAuthToken = (loginResponse: LoginResponse): void => {
  try {
    if (!loginResponse.successful || !loginResponse.token) {
      throw new Error('Login response is not successful or missing token');
    }

    const token = loginResponse.token;
    const expiresAt = extractExpirationFromToken(token);
    const user = extractUserFromToken(token);

    const tokenData: StoredTokenData = {
      token,
      expiresAt,
      user,
    };

    localStorage.setItem(TOKEN_KEY, tokenData.token);
    localStorage.setItem(TOKEN_EXPIRY_KEY, tokenData.expiresAt);
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(tokenData.user));

    console.log('Auth token stored successfully', { 
      user: tokenData.user,
      expiresAt: tokenData.expiresAt,
      expiresIn: new Date(tokenData.expiresAt).toISOString()
    });
  } catch (error) {
    console.error('Error storing auth token:', error);
    throw new Error('Failed to store authentication token');
  }
};

/**
 * Retrieve the stored authentication token
 */
export const getStoredToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Error retrieving stored token:', error);
    return null;
  }
};

/**
 * Retrieve stored user data
 */
export const getStoredUserData = (): AuthToken['user'] | null => {
  try {
    const userData = localStorage.getItem(USER_DATA_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error retrieving stored user data:', error);
    return null;
  }
};

/**
 * Get token expiration timestamp
 */
export const getTokenExpiry = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_EXPIRY_KEY);
  } catch (error) {
    console.error('Error retrieving token expiry:', error);
    return null;
  }
};

/**
 * Check if token is expired or will expire soon
 */
export const isTokenExpired = (): boolean => {
  try {
    const expiryStr = getTokenExpiry();
    if (!expiryStr) {
      console.log('No expiry string found, considering token expired');
      return true;
    }

    const expiryDate = new Date(expiryStr);
    const now = new Date();
    const timeUntilExpiry = expiryDate.getTime() - EXPIRY_BUFFER_MS - now.getTime();
    
    console.log('Token expiration check:', {
      expiryDate: expiryDate.toISOString(),
      now: now.toISOString(),
      timeUntilExpiry: timeUntilExpiry,
      timeUntilExpiryMinutes: Math.round(timeUntilExpiry / (1000 * 60)),
      isExpired: timeUntilExpiry <= 0
    });
    
    // Consider token expired if it expires within the buffer time
    return timeUntilExpiry <= 0;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

/**
 * Check if user has a valid token
 */
export const hasValidToken = (): boolean => {
  const token = getStoredToken();
  if (!token) return false;
  
  return !isTokenExpired();
};

/**
 * Clear all stored authentication data and cached data
 */
export const clearAuthData = (): void => {
  try {
    // Clear authentication data
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    
    // Clear cached data
    localStorage.removeItem('cached_contact_data');
    localStorage.removeItem('cached_districts_data');
    localStorage.removeItem('cached_permit_requests');
    localStorage.removeItem('offline_permit_requests');
    localStorage.removeItem('last_permit_request');
    localStorage.removeItem('permit_request_queue');
    
    // Clear session storage
    sessionStorage.removeItem('deepLinkUrl');
    sessionStorage.removeItem('resetCode');
    
    console.log('Auth data and cached data cleared successfully');
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }
};

/**
 * Get complete stored auth data
 */
export const getStoredAuthData = (): StoredTokenData | null => {
  try {
    const token = getStoredToken();
    const expiry = getTokenExpiry();
    const user = getStoredUserData();

    if (!token || !expiry || !user) {
      return null;
    }

    return {
      token,
      expiresAt: expiry,
      user,
    };
  } catch (error) {
    console.error('Error retrieving stored auth data:', error);
    return null;
  }
};

/**
 * Update stored user data
 */
export const updateStoredUserData = (userData: Partial<AuthToken['user']>): void => {
  try {
    const currentUser = getStoredUserData();
    if (!currentUser) {
      throw new Error('No existing user data to update');
    }

    const updatedUser = { ...currentUser, ...userData };
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(updatedUser));
    console.log('User data updated successfully');
  } catch (error) {
    console.error('Error updating stored user data:', error);
    throw new Error('Failed to update user data');
  }
};

/**
 * Get time until token expires in milliseconds
 */
export const getTimeUntilExpiry = (): number => {
  try {
    const expiryStr = getTokenExpiry();
    if (!expiryStr) return 0;

    const expiryDate = new Date(expiryStr);
    const now = new Date();
    
    return Math.max(0, expiryDate.getTime() - now.getTime());
  } catch (error) {
    console.error('Error calculating time until expiry:', error);
    return 0;
  }
};

/**
 * Check if token will expire soon (within 10 minutes)
 */
export const isTokenExpiringSoon = (): boolean => {
  const timeUntilExpiry = getTimeUntilExpiry();
  const tenMinutes = 10 * 60 * 1000;
  return timeUntilExpiry > 0 && timeUntilExpiry <= tenMinutes;
};

/**
 * Get the current user's customer ID
 */
export const getCurrentCustomerId = (): string | null => {
  try {
    const user = getStoredUserData();
    console.log('🔍 Getting customer ID:', {
      user: user,
      customerId: user?.customerId,
      hasUser: !!user,
      timestamp: new Date().toISOString()
    });
    return user?.customerId || null;
  } catch (error) {
    console.error('❌ Error getting customer ID:', error);
    return null;
  }
}; 